import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import ManagerNavbar from "../Layout/ManagerNavbar";

/* ─── Google Fonts ─────────────────────────────────── */
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap";
document.head.appendChild(fontLink);

/* ─── Styles ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("rlf-styles")) return;
  const style = document.createElement("style");
  style.id = "rlf-styles";
  style.textContent = `
    :root {
      --primary: #2F5BEA;
      --primary-light: #EEF2FF;
      --primary-dark: #1E3FAD;
      --surface: #FFFFFF;
      --bg: #F0F3F9;
      --border: #E2E6EF;
      --text: #111827;
      --text-secondary: #6B7480;
      --danger: #EF4444;
      --success: #10B981;
      --warning: #F59E0B;
      --shadow-sm: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05);
      --shadow-md: 0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.05);
      --shadow-lg: 0 10px 40px rgba(0,0,0,0.12);
      --radius: 12px;
      --radius-sm: 8px;
      --font-head: 'Sora', sans-serif;
      --font-body: 'DM Sans', sans-serif;
    }

    .rlf-wrap * {
      font-family: var(--font-body);
      box-sizing: border-box;
    }

    .rlf-wrap {
      min-height: 100vh;
      background: var(--bg);
      padding-top: 80px;
    }

    /* ── Grid Layout ── */
    .rlf-layout {
      display: grid;
      grid-template-columns: 1fr 280px;
      gap: 24px;
      max-width: 1100px;
      margin: 0 auto;
      padding: 32px 24px 48px;
      align-items: start;
    }

    /* ── Card ── */
    .rlf-card {
      background: var(--surface);
      border-radius: var(--radius);
      box-shadow: var(--shadow-md);
      border: 1px solid var(--border);
      overflow: hidden;
    }

    .rlf-card-header {
      padding: 20px 28px;
      border-bottom: 1px solid var(--border);
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(135deg, #FAFBFF 0%, #F5F7FF 100%);
    }

    .rlf-card-header h2 {
      font-family: var(--font-head);
      font-size: 1.15rem;
      font-weight: 600;
      color: var(--text);
      margin: 0;
      letter-spacing: -0.02em;
    }

    .rlf-back-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 0.82rem;
      font-weight: 500;
      color: var(--primary);
      cursor: pointer;
      border: 1px solid var(--primary-light);
      background: var(--primary-light);
      padding: 5px 12px;
      border-radius: 20px;
      transition: all 0.2s;
      text-decoration: none;
    }
    .rlf-back-btn:hover {
      background: var(--primary);
      color: white;
      border-color: var(--primary);
    }

    .rlf-card-body { padding: 28px; }

    /* ── Form Fields ── */
    .rlf-form-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }

    .rlf-field { display: flex; flex-direction: column; gap: 6px; }
    .rlf-field.span-2 { grid-column: span 2; }
    .rlf-field.span-3 { grid-column: span 3; }

    .rlf-label {
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--text-secondary);
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    .rlf-input,
    .rlf-select,
    .rlf-textarea {
      width: 100%;
      padding: 10px 14px;
      border: 1.5px solid var(--border);
      border-radius: var(--radius-sm);
      font-size: 0.9rem;
      font-family: var(--font-body);
      color: var(--text);
      background: #FAFBFC;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
      outline: none;
      appearance: none;
    }
    .rlf-input:focus,
    .rlf-select:focus,
    .rlf-textarea:focus {
      border-color: var(--primary);
      background: #fff;
      box-shadow: 0 0 0 3px rgba(47, 91, 234, 0.1);
    }
    .rlf-input.error,
    .rlf-select.error,
    .rlf-textarea.error {
      border-color: var(--danger);
      background: #FFF5F5;
    }
    .rlf-textarea { resize: vertical; min-height: 110px; line-height: 1.6; }

    .rlf-select-wrap {
      position: relative;
    }
    .rlf-select-wrap::after {
      content: '';
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 6px solid var(--text-secondary);
      pointer-events: none;
    }
    .rlf-select { padding-right: 36px; cursor: pointer; }

    .rlf-error-msg {
      font-size: 0.76rem;
      color: var(--danger);
      font-weight: 500;
      margin-top: 2px;
    }

    /* ── Days Badge ── */
    .rlf-days-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: linear-gradient(135deg, #EEF2FF, #E0E7FF);
      border: 1px solid #C7D2FE;
      border-radius: 8px;
      padding: 10px 16px;
      font-size: 0.88rem;
      color: #3730A3;
      font-weight: 500;
    }
    .rlf-days-badge strong {
      font-family: var(--font-head);
      font-size: 1.1rem;
      font-weight: 700;
    }

    /* ── Alert ── */
    .rlf-alert {
      padding: 12px 16px;
      border-radius: var(--radius-sm);
      font-size: 0.87rem;
      font-weight: 500;
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .rlf-alert.success {
      background: #ECFDF5; color: #065F46; border: 1px solid #A7F3D0;
    }
    .rlf-alert.error {
      background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA;
    }

    /* ── Buttons ── */
    .rlf-btn-row {
      display: flex;
      gap: 12px;
      margin-top: 8px;
    }
    .rlf-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 10px 24px;
      border-radius: var(--radius-sm);
      font-size: 0.875rem;
      font-weight: 600;
      font-family: var(--font-body);
      cursor: pointer;
      border: none;
      transition: all 0.2s;
      letter-spacing: 0.01em;
    }
    .rlf-btn-primary {
      background: var(--primary);
      color: white;
      box-shadow: 0 2px 8px rgba(47,91,234,0.3);
    }
    .rlf-btn-primary:hover {
      background: var(--primary-dark);
      box-shadow: 0 4px 14px rgba(47,91,234,0.4);
      transform: translateY(-1px);
    }
    .rlf-btn-ghost {
      background: transparent;
      color: var(--text-secondary);
      border: 1.5px solid var(--border);
    }
    .rlf-btn-ghost:hover {
      background: var(--bg);
      color: var(--text);
      border-color: #C4CAD6;
    }

    /* ── Summary Sidebar ── */
    .rlf-summary-card {
      background: var(--surface);
      border-radius: var(--radius);
      box-shadow: var(--shadow-md);
      border: 1px solid var(--border);
      overflow: hidden;
      position: sticky;
      top: 96px;
    }
    .rlf-summary-header {
      padding: 14px 18px;
      background: linear-gradient(135deg, #2F5BEA, #4B72F0);
      color: white;
    }
    .rlf-summary-header h3 {
      font-family: var(--font-head);
      font-size: 0.88rem;
      font-weight: 600;
      margin: 0;
      letter-spacing: 0.02em;
    }
    .rlf-summary-header p {
      font-size: 0.72rem;
      opacity: 0.75;
      margin: 2px 0 0;
    }
    .rlf-summary-body { padding: 4px 0 8px; }

    .rlf-leave-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 18px;
      transition: background 0.15s;
    }
    .rlf-leave-row:hover { background: var(--bg); }

    .rlf-leave-row-label {
      font-size: 0.82rem;
      color: var(--text);
      font-weight: 500;
    }
    .rlf-leave-row-meta {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 1px;
    }
    .rlf-remaining {
      font-family: var(--font-head);
      font-size: 0.92rem;
      font-weight: 700;
      color: var(--primary);
    }
    .rlf-applied {
      font-size: 0.7rem;
      color: var(--text-secondary);
    }
    .rlf-divider { height: 1px; background: var(--border); margin: 4px 18px; }
    .rlf-summary-total {
      display: flex;
      justify-content: space-between;
      padding: 10px 18px;
      font-size: 0.83rem;
      font-weight: 600;
      color: var(--text);
      background: #F8F9FC;
    }
    .rlf-summary-total .total-count {
      font-family: var(--font-head);
      font-size: 1rem;
      font-weight: 700;
      color: var(--primary);
    }

    /* ── Modal ── */
    .rlf-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(17,24,39,0.55);
      backdrop-filter: blur(3px);
      z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
      animation: fadeIn 0.2s ease;
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

    .rlf-modal {
      background: var(--surface);
      border-radius: 16px;
      box-shadow: var(--shadow-lg);
      width: 100%; max-width: 440px;
      overflow: hidden;
      animation: slideUp 0.25s ease;
    }
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .rlf-modal-header {
      padding: 18px 22px 14px;
      display: flex;
      align-items: flex-start;
      gap: 14px;
      border-bottom: 1px solid var(--border);
    }
    .rlf-modal-icon {
      width: 40px; height: 40px;
      border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.2rem;
      flex-shrink: 0;
    }
    .rlf-modal-icon.warn { background: #FFF7ED; color: #D97706; }
    .rlf-modal-icon.danger { background: #FEF2F2; color: #DC2626; }

    .rlf-modal-title-group h5 {
      font-family: var(--font-head);
      font-size: 1rem;
      font-weight: 600;
      color: var(--text);
      margin: 0 0 2px;
    }
    .rlf-modal-title-group p {
      font-size: 0.78rem;
      color: var(--text-secondary);
      margin: 0;
    }
    .rlf-modal-close {
      margin-left: auto;
      background: none;
      border: none;
      font-size: 1.1rem;
      color: var(--text-secondary);
      cursor: pointer;
      padding: 2px;
      border-radius: 4px;
      transition: color 0.15s;
    }
    .rlf-modal-close:hover { color: var(--text); }

    .rlf-modal-body { padding: 18px 22px; }
    .rlf-modal-body p {
      font-size: 0.88rem; color: #374151; line-height: 1.6; margin: 0 0 12px;
    }

    .rlf-switch-list {
      list-style: none; padding: 0; margin: 0;
      display: flex; flex-direction: column; gap: 6px;
    }
    .rlf-switch-item {
      display: flex; align-items: center; justify-content: space-between;
      padding: 11px 14px;
      border: 1.5px solid var(--border);
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: all 0.2s;
      background: #FAFBFC;
    }
    .rlf-switch-item:hover {
      border-color: var(--primary);
      background: var(--primary-light);
    }
    .rlf-switch-item-name {
      font-size: 0.87rem; font-weight: 600; color: var(--text);
    }
    .rlf-switch-item-days {
      font-size: 0.78rem;
      background: #E0E7FF; color: #3730A3;
      padding: 2px 9px; border-radius: 20px; font-weight: 600;
    }

    .rlf-modal-footer {
      padding: 14px 22px;
      display: flex; justify-content: flex-end; gap: 10px;
      border-top: 1px solid var(--border);
      background: #FAFBFC;
    }

    .rlf-empty {
      text-align: center; padding: 20px;
      font-size: 0.83rem; color: var(--text-secondary);
    }

    @media (max-width: 900px) {
      .rlf-layout {
        grid-template-columns: 1fr;
      }
      .rlf-summary-card { position: static; }
      .rlf-form-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 600px) {
      .rlf-form-grid { grid-template-columns: 1fr; }
      .rlf-field.span-2, .rlf-field.span-3 { grid-column: span 1; }
    }
  `;
  document.head.appendChild(style);
};

/* ═══════════════════════════════════════════════════════ */
const ManagerLeaveAppli = () => {
  injectStyles();

  const [form, setForm] = useState({
    leaveType: "", startDate: "", endDate: "", reason: "", document: null,
  });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const [leaveSummary, setLeaveSummary] = useState([]);
  const [showLOPModal, setShowLOPModal] = useState(false);
  const [lopMessage, setLopMessage] = useState("");
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [availableLeaves, setAvailableLeaves] = useState([]);
  const [financialYear, setFinancialYear] = useState("");
  const navigate = useNavigate();
  const selectedColor = localStorage.getItem("empnavbarColor") || "#2F5BEA";
  const userId = localStorage.getItem("employeeId");

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  const fetchLeaveSummary = async () => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    try {
      const [typesRes, summaryRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/leave-types/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/leave-transactions/summary/${userId}`, {
          params: { financialYear }, headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setLeaveTypes(typesRes.data);
      setLeaveSummary(summaryRes.data);
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) { localStorage.removeItem("token"); navigate("/"); }
    }
  };

  useEffect(() => { if (financialYear) fetchLeaveSummary(); }, [financialYear]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/company-info`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => {
        const company = Array.isArray(data) ? data[0] : data;
        if (company?.financialYear) setFinancialYear(company.financialYear);
      })
      .catch(console.error);
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm(prev => ({ ...prev, [name]: name === "document" ? files[0] : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const calculateDaysCount = () => {
    if (!form.startDate || !form.endDate) return 0;
    const diff = new Date(form.endDate) - new Date(form.startDate);
    return diff >= 0 ? Math.floor(diff / 86400000) + 1 : 0;
  };

  const validate = () => {
    const e = {};
    if (!form.leaveType) e.leaveType = "Please select a leave type.";
    if (!form.startDate) e.startDate = "Start date is required.";
    if (!form.endDate) e.endDate = "End date is required.";
    if (form.startDate && form.endDate && form.endDate < form.startDate)
      e.endDate = "End date cannot be before start date.";
    if (!form.reason.trim()) e.reason = "Please provide a reason.";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    setSubmitStatus(null);
    if (Object.keys(validationErrors).length > 0) return;

    const requestedDays = calculateDaysCount();
    const selectedLeave = leaveSummary.find(l => l.leaveType === form.leaveType);
    const remaining = selectedLeave ? selectedLeave.remaining : 0;

    if (remaining >= requestedDays) { submitForm(); return; }

    const other = leaveSummary.filter(l => l.leaveType !== form.leaveType && l.remaining > 0);
    if (other.length > 0) { setAvailableLeaves(other); setShowSwitchModal(true); }
    else {
      setForm(prev => ({ ...prev, leaveType: "Loss of Pay(LOP)" }));
      setLopMessage(`You have no credited leave remaining. All ${requestedDays} day(s) will be marked as Loss of Pay (LOP).`);
      setShowLOPModal(true);
    }
  };

  const submitForm = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/api/leave-transactions/apply`,
        { leaveType: form.leaveType, startDate: form.startDate, endDate: form.endDate, reason: form.reason, financialYear },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitStatus("success");
      await fetchLeaveSummary();
      setTimeout(() => navigate("/managerleaves"), 1200);
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) { localStorage.removeItem("token"); navigate("/"); }
      setSubmitStatus("error");
    }
  };

  const daysCount = calculateDaysCount();
  const totalRemaining = leaveSummary.reduce((s, l) => s + l.remaining, 0);
  const totalApplied = leaveSummary.reduce((s, l) => s + l.applied, 0);
  const today = new Date().toISOString().split("T")[0];

  return (
    <>
      <div className="fixed-top"><ManagerNavbar /></div>

      <div className="rlf-wrap">
        <div className="rlf-layout">

          {/* ── Main Form ── */}
          <div>
            <div className="rlf-card">
              <div className="rlf-card-header">
                <h2>Leave Application</h2>
                <span className="rlf-back-btn" onClick={() => navigate("/managerleaves")}>
                 List
                </span>
              </div>

              <div className="rlf-card-body">
                {submitStatus === "success" && (
                  <div className="rlf-alert success">✅ Application submitted successfully! Redirecting…</div>
                )}
                {submitStatus === "error" && (
                  <div className="rlf-alert error">❌ Something went wrong. Please try again.</div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div className="rlf-form-grid">

                    {/* Leave Type */}
                    <div className="rlf-field">
                      <label className="rlf-label">Leave Type</label>
                      <div className="rlf-select-wrap">
                        <select
                          name="leaveType"
                          value={form.leaveType}
                          onChange={handleChange}
                          className={`rlf-select ${errors.leaveType ? "error" : ""}`}
                        >
                          <option value="">Select type…</option>
                          {leaveTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                      </div>
                      {errors.leaveType && <span className="rlf-error-msg">{errors.leaveType}</span>}
                    </div>

                    {/* Start Date */}
                    <div className="rlf-field">
                      <label className="rlf-label">Start Date</label>
                      <input type="date" name="startDate" value={form.startDate}
                        onChange={handleChange} min={today}
                        className={`rlf-input ${errors.startDate ? "error" : ""}`} />
                      {errors.startDate && <span className="rlf-error-msg">{errors.startDate}</span>}
                    </div>

                    {/* End Date */}
                    <div className="rlf-field">
                      <label className="rlf-label">End Date</label>
                      <input type="date" name="endDate" value={form.endDate}
                        onChange={handleChange} min={form.startDate || today}
                        className={`rlf-input ${errors.endDate ? "error" : ""}`} />
                      {errors.endDate && <span className="rlf-error-msg">{errors.endDate}</span>}
                    </div>

                    {/* Reason */}
                    <div className="rlf-field span-3">
                      <label className="rlf-label">Reason for Leave</label>
                      <textarea name="reason" value={form.reason} onChange={handleChange}
                        placeholder="Briefly describe the reason for your leave request…"
                        className={`rlf-textarea ${errors.reason ? "error" : ""}`} />
                      {errors.reason && <span className="rlf-error-msg">{errors.reason}</span>}
                    </div>

                    {/* Days Count */}
                    {daysCount > 0 && (
                      <div className="rlf-field span-3">
                        <div className="rlf-days-badge">
                          <span>📅</span>
                          <span>Duration: <strong>{daysCount}</strong> day{daysCount > 1 ? "s" : ""}</span>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="rlf-field span-3">
                      <div className="rlf-btn-row">
                        <button type="submit" className="rlf-btn rlf-btn-primary">
                          Submit Application
                        </button>
                        <button type="button" className="rlf-btn rlf-btn-ghost"
                          onClick={() => navigate(-1)}>
                          Cancel
                        </button>
                      </div>
                    </div>

                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* ── Summary Sidebar ── */}
          <div className="rlf-summary-card">
            <div className="rlf-summary-header">
              <h3>Leave Summary</h3>
              <p>Remaining [ Applied ]</p>
            </div>
            <div className="rlf-summary-body">
              {leaveSummary.length === 0
                ? <div className="rlf-empty">No leave records found.</div>
                : leaveSummary.map((leave, i) => (
                  <div key={leave.leaveType}>
                    <div className="rlf-leave-row">
                      <span className="rlf-leave-row-label">{leave.leaveType}</span>
                      <div className="rlf-leave-row-meta">
                        <span className="rlf-remaining">{leave.remaining}</span>
                        <span className="rlf-applied">{leave.applied} Applied</span>
                      </div>
                    </div>
                    {i < leaveSummary.length - 1 && <div className="rlf-divider" />}
                  </div>
                ))
              }
              {leaveSummary.length > 0 && (
                <>
                  <div className="rlf-divider" style={{ margin: "8px 18px" }} />
                  <div className="rlf-summary-total">
                    <span>Total</span>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <span className="total-count">{totalRemaining}</span>
                      <span style={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>{totalApplied} Applied</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── Switch Leave Modal ── */}
      {showSwitchModal && (
        <div className="rlf-modal-overlay">
          <div className="rlf-modal">
            <div className="rlf-modal-header">
              <div className="rlf-modal-icon warn">⚠️</div>
              <div className="rlf-modal-title-group">
                <h5>Insufficient Leave Balance</h5>
                <p>You can switch to another available type</p>
              </div>
              <button className="rlf-modal-close" onClick={() => setShowSwitchModal(false)}>✕</button>
            </div>
            <div className="rlf-modal-body">
              <p>
                Not enough <strong>{form.leaveType}</strong> balance for this request.
                You have remaining balance in other leave types — select one below, or proceed with LOP.
              </p>
              <ul className="rlf-switch-list">
                {availableLeaves.map(l => (
                  <li key={l.leaveType} className="rlf-switch-item"
                    onClick={() => { setForm({ ...form, leaveType: l.leaveType }); setShowSwitchModal(false); }}>
                    <span className="rlf-switch-item-name">{l.leaveType}</span>
                    <span className="rlf-switch-item-days">{l.remaining} d left</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rlf-modal-footer">
               <button className="rlf-btn rlf-btn-primary" onClick={() => {
                setForm(prev => ({ ...prev, leaveType: "Loss of Pay(LOP)" }));
                setShowSwitchModal(false); setShowLOPModal(true);
              }}>Proceed with LOP</button>
              <button className="rlf-btn rlf-btn-ghost" onClick={() => setShowSwitchModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOP Modal ── */}
      {showLOPModal && (
        <div className="rlf-modal-overlay">
          <div className="rlf-modal">
            <div className="rlf-modal-header">
              <div className="rlf-modal-icon danger">🔴</div>
              <div className="rlf-modal-title-group">
                <h5>Loss of Pay Alert</h5>
                <p>This action will affect your salary</p>
              </div>
              <button className="rlf-modal-close" onClick={() => setShowLOPModal(false)}>✕</button>
            </div>
            <div className="rlf-modal-body">
              <p>{lopMessage}</p>
            </div>
            <div className="rlf-modal-footer">
              <button className="rlf-btn rlf-btn-primary" onClick={() => { setShowLOPModal(false); submitForm(); }}>
                Confirm &amp; Proceed
              </button>
              <button className="rlf-btn rlf-btn-ghost" onClick={() => setShowLOPModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManagerLeaveAppli;